import * as R from 'ramda';
import axios from 'axios';
import { useRef, useState } from 'react';
import i18n from 'helpers/i18n';
import messages from './messages';
import useAsync from 'hooks/useAsync';
import useCurrentUser from 'hooks/useCurrentUser';
import {
  activateProductKey,
  ProductKeyPayload,
  TokenRedemptionConfirmation
} from 'actions/productKeys';
import { useNavigate } from 'react-router-dom';
import { getPostActivationData } from './utils';

function useProductKey() {
  const asyncRequest = useAsync();
  const currentUser = useCurrentUser();
  const [error, setError] = useState('');
  const [pendingConfirmation, setPendingConfirmation] = useState<{
    response: TokenRedemptionConfirmation;
    payload: ProductKeyPayload;
  } | null>(null);
  const submitting = useRef(false);
  const activationElement = useRef<HTMLElement | null>(null);
  const navigate = useNavigate();

  function setErrors(message: string) {
    setError(message);
    asyncRequest.setError(message);
  }

  function submit(productKey: string) {
    if (submitting.current) return;
    setPendingConfirmation(null);
    activationElement.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    if (R.isNil(productKey) || R.isEmpty(productKey)) {
      setErrors(i18n.ft(messages.errors.required));
      return;
    }

    const data: ProductKeyPayload = {
      key: productKey,
      user: {
        email: currentUser.email,
        first_name: currentUser.first_name,
        last_name: currentUser.last_name,
        already_in_okta: true
      }
    };

    redeemToken(data);
  }

  function redeemToken(data: ProductKeyPayload) {
    if (submitting.current) return;
    submitting.current = true;
    setError('');

    let confirmation: TokenRedemptionConfirmation | null = null;
    const request = activateProductKey(data).catch((requestError: unknown) => {
      if (
        axios.isAxiosError<TokenRedemptionConfirmation>(requestError) &&
        requestError.response?.status === 409 &&
        requestError.response.data.code === 'confirmation_required' &&
        requestError.response.data.reason ===
          'individual_calibration_token_already_redeemed'
      ) {
        confirmation = requestError.response.data;
      }
      throw requestError;
    });

    asyncRequest.run(request, {
      onSuccess: (response: any) => {
        submitting.current = false;
        setPendingConfirmation(null);
        const { source, path, state } = getPostActivationData(response);

        if (source === 'external') {
          window.location.href = path;
        } else {
          navigate(path, { state });
        }
      },
      onError: (error: any) => {
        submitting.current = false;
        if (confirmation) {
          asyncRequest.reset();
          setPendingConfirmation({ response: confirmation, payload: data });
          return;
        }

        setPendingConfirmation(null);
        if (!R.isNil(error.errors?.product_key)) {
          setErrors(error.errors.product_key[0]);
        } else if (!R.isNil(error.errors?.user_token?.[0]?.user_test)) {
          setErrors(error.errors.user_token[0].user_test[0]);
        } else if (!R.isNil(error.errors?.user_token)) {
          setErrors(error.errors.user_token[0]);
        } else if (!R.isNil(error.errors?.user)) {
          setErrors(error.errors.user[0]);
        } else if (!R.isNil(error.errors?.base)) {
          setErrors(i18n.ft(messages.errors.ineligible));
        } else if (!R.isNil(error.message)) {
          setErrors(i18n.ft(messages.errors.noAvailable));
        } else {
          setErrors(i18n.ft(messages.errors.unknownError));
        }
      }
    });
  }

  function reset() {
    setPendingConfirmation(null);
    setError('');
    asyncRequest.reset();
  }

  function confirmTokenRedemption() {
    if (!pendingConfirmation || submitting.current) return;
    redeemToken({
      ...pendingConfirmation.payload,
      confirm_token_redemption: true
    });
  }

  function cancelTokenRedemption() {
    if (submitting.current) return;
    reset();
    requestAnimationFrame(() => {
      if (activationElement.current?.isConnected) {
        activationElement.current.focus();
      }
    });
  }

  return {
    ...asyncRequest,
    error,
    submit,
    reset,
    pendingConfirmation: pendingConfirmation?.response ?? null,
    confirmTokenRedemption,
    cancelTokenRedemption
  };
}

export default useProductKey;
